<template>
	<div v-if="questions.length" class="card-page questionnaire-wrapper">
		<ja-header
			ref="header"
			:title="$t('gamePlayMetaTitles.questionnaire')"
			subtitle="JA Finance Park"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/questionnaire"></svgicon>
			</template>
		</ja-header>
		<content>
			<ja-passcode-modal @onLifeStageSummary="isLifeStageSummary">
			</ja-passcode-modal>
			<div class="question-wrapper">
				<ja-lifestyle-question
					ref="questionComponent"
					:question-id="questions[currentQuestionIndex].lifestyle_Question_ID"
					:question-info="questions[currentQuestionIndex]"
					:selected-answer="selectedAnswer"
					@answerSelected="onAnswerSelected"
				></ja-lifestyle-question>
			</div>
		</content>
		<footer>
			<div class="footer-item">
				<ja-local-sponsor
					v-if="localSponsor"
					:logo="localSponsor"
				></ja-local-sponsor>
			</div>
			<ja-progress-tracker
				v-if="questions.length > 1"
				class="footer-item"
				:current-page="currentQuestionIndex"
				:num-pages="questions.length"
			></ja-progress-tracker>

			<div class="footer-item">
				<ja-button
					v-if="currentQuestionIndex == questions.length - 1"
					class="small right"
					:aria-label="$t('submitAnswers')"
					:disabled="isAnswerEmpty(selectedAnswer) || requestMade"
					:aria-disabled="isAnswerEmpty(selectedAnswer) || requestMade"
					name="complete"
					@click.native="sendAnswers"
					><span class="button-text">{{ $t('complete') }}</span>
					<svgicon name="arrow-right" class="arrow-right"></svgicon
				></ja-button>
				<ja-button
					v-else
					class="small right"
					name="next"
					:aria-disabled="isAnswerEmpty(selectedAnswer)"
					:aria-label="$t('goToNextQuestion')"
					@click.native="goNextQuestion"
					><span class="button-text">{{ $t('next') }}</span>
					<svgicon name="arrow-right" class="arrow-right"></svgicon>
				</ja-button>
			</div>
		</footer>
	</div>
</template>
<script>
import '../components/icons/headerIcons/questionnaire';
import gql from 'graphql-tag';
import Button from '../components/ui/Button';
import Header from '../components/ui/headers/Header';
import ProgressTracker from '../components/ui/ProgressTracker';
import LifestyleQuestion from '../components/LifestyleQuestion/LifestyleQuestion';
import UserData from '../mixins/UserData';
import NavigationLoading from '../mixins/NavigationLoading';
import LocalSponsor from '@/components/ui/LocalSponsor';
import LocalSponsorService from '@/services/LocalSponsorService';
import { convertDateTimeToUTC } from '@/helpers/utils';
import { ValidateProfileStepBeforeProceed, ProfileData } from '@/mixins';
import {
	profileCreationSteps,
	answersType,
	simulationNameType
} from '@/helpers/constants';
import PasscodeModal from '@/components/Modal/PasscodeModal';

export default {
	components: {
		jaLifestyleQuestion: LifestyleQuestion,
		jaButton: Button,
		jaHeader: Header,
		jaProgressTracker: ProgressTracker,
		jaLocalSponsor: LocalSponsor,
		jaPasscodeModal: PasscodeModal
	},
	mixins: [
		UserData,
		NavigationLoading,
		ValidateProfileStepBeforeProceed,
		ProfileData
	],
	props: {
		type: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			questions: [],
			selectedAnswer: null,
			currentQuestionIndex: 0,
			currentQuestionID: null,
			lastAnsweredQuestion: 0,
			requestMade: false,
			localSponsor: '',
			skipQuery: false,
			profileCreationSteps: profileCreationSteps,
			simulationType: null
		};
	},
	computed: {
		isSimulationCompleted() {
			return this.$store.getters.isSimulationCompleted;
		},
		answers() {
			const answers = {};
			if (this.questions.length) {
				for (var i = 0, len = this.questions.length; i < len; i++) {
					let currentQuestionID = this.questions[i].lifestyle_Question_ID;

					answers[currentQuestionID] = {
						type: this.questions[i].cmsLifestyleQuestionFormat.questionFormat,
						value: null
					};
				}
			}

			return answers;
		}
	},
	watch: {
		answers(data) {
			if (Object.keys(data).length) {
				this.currentQuestionIndex = 0;
			}
		},
		$route() {
			this.selectedAnswer = null;
			// set skipQuery to false, since a couple of routes depends on this component
			// after skipQuery is set to false in the first component's usage
			// this causes constant loading screen (on next usage) because
			// the content is not updated by 'refetch', and hiding the loading element
			// depends on that
			this.skipQuery = false;
			this.$apollo.queries.questions.refetch();
		}
	},
	created() {
		if (this.userData) {
			this.checkForProfileData();
			this.$store.dispatch('getPauseState');
		}
	},
	mounted() {
		this.simulationType = this.$store.getters.simulationType;
		LocalSponsorService.getLogo(
			'PreAndPostSimulationQuestions',
			this.userData.studentFacilityId
		).then(logo => (this.localSponsor = logo));

		if (this.$store.getters.isIrl) {
			if (this.type === profileCreationSteps.KNOWLEDGE) {
				this.$store.dispatch('hideModalLifeStageSummary');
				this.markKnowledgeQuestionAsAnswered();
			}
		} else {
			if (this.type === profileCreationSteps.KNOWLEDGE) {
				this.$store.dispatch('hideModalLifeStageSummary');
				this.$store.dispatch('showModalPassCode');
			}
		}
	},
	apollo: {
		questions: {
			query: gql`
				query questions($questionsFilter: String!) {
					cmsLifestyleQuestions(
						where: $questionsFilter
						order: "questionOrder"
					) {
						lifestyle_Question_ID
						questionOrder
						question
						lifeStage_ID
						cmsLifestyleQuestionType {
							questionType
						}
						cmsLifestyleQuestionFormat {
							questionFormat
						}
						cmsLifestyleQuestionAnswerOptions {
							lifestyle_Question_Option_ID
							text
							optionOrder
						}
					}
				}
			`,
			fetchPolicy: 'no-cache',
			skip() {
				return !this.type || this.skipQuery || !this.userData;
			},
			variables() {
				return {
					questionsFilter: this.getQuestionsFilters()
				};
			},
			update: function(data) {
				const filteredQuestions = data.cmsLifestyleQuestions.filter(
					(question, index, self) =>
						index === self.findIndex(q => q.question === question.question)
				);
				if (
					this.type !== profileCreationSteps.KNOWLEDGE ||
					this.simulationType !== simulationNameType.ADVANCED_VIRTUAL ||
					this.simulationType !== simulationNameType.ENTRY_VIRTUAL
				) {
					this.$store.commit('setLoading', false);
				}

				return filteredQuestions;
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		this.skipQuery = true;
		next();
	},
	methods: {
		isLifeStageSummary(onValue) {
			if (onValue && this.type === profileCreationSteps.KNOWLEDGE) {
				this.$store.commit('setLoading', false);
			}
		},
		onAnswerSelected(data) {
			if (this.answers[data.question].type == answersType.MULTI_SELECT) {
				if (!this.answers[data.question].value) {
					this.answers[data.question].value = [];
				}
				if (this.answers[data.question].value.includes(data.answer)) {
					this.answers[data.question].value = this.answers[
						data.question
					].value.filter(item => item !== data.answer);
				} else {
					this.answers[data.question].value.push(data.answer);
				}
			} else {
				this.answers[data.question].value = data.answer;
			}

			this.selectedAnswer = this.answers[data.question].value;
		},
		goNextQuestion() {
			if (
				!this.isAnswerEmpty(this.selectedAnswer) &&
				this.currentQuestionIndex < this.questions.length - 1
			) {
				this.selectedAnswer = null;
				if (this.currentQuestionIndex == this.lastAnsweredQuestion) {
					this.lastAnsweredQuestion++;
				}
				this.currentQuestionIndex++;
			}
		},
		async sendAnswers() {
			this.$store.commit('setLoading', true);

			// use KNOWLEDGE query from constants becuase this component is used
			// in Pre-test, post-test and knowledge and the validation query is the same
			const alreadyAnsweredQuestions = await this.checkProfileCreationFor(
				this.profileCreationSteps.KNOWLEDGE,
				this.userData.studentId
			);

			this.requestMade = true;

			let list = [];
			for (let key in this.answers) {
				let obj = this.answers[key];
				let answerObj = {};
				answerObj.isPost = this.type == profileCreationSteps.POST_TEST;
				answerObj.studentId = this.userData.studentId;
				answerObj.simulation_Lifestyle_Questions_ID = key;
				switch (obj.type) {
					case 'Multiple options':
						answerObj.lifestyle_Question_Option_ID = obj.value;
						list.push(answerObj);
						break;
					case 'Multi Select':
						obj.value.forEach(value => {
							let multiAnswerObj = { ...answerObj };
							multiAnswerObj.lifestyle_Question_Option_ID = value;
							list.push(multiAnswerObj);
						});
						break;
					case 'Text Only':
						answerObj.otherOptionAnswer = obj.value;
						list.push(answerObj);
						break;
					case 'True / False':
						answerObj.boolOptionAnswer = obj.value;
						list.push(answerObj);
						break;
				}
			}

			// remap the response of the check query to get only the questionIds
			const answeredQuestions = alreadyAnsweredQuestions.map(i => i.questionId);

			// set default falue. the value may or may not be set later on
			let response = null;

			// filter list for mutation to include only those questions
			// that are NOT already answered
			if (answeredQuestions.length) {
				list = list.filter(
					i => !answeredQuestions.includes(i.simulation_Lifestyle_Questions_ID)
				);
			}

			// Use variable as it is more desciptive
			const isMutationListEmpty = !list.length;

			// if list is not empty, proceed as usual
			if (!isMutationListEmpty) {
				response = await this.$apollo.mutate({
					mutation: gql`
						mutation($list: [StudentQuestionAnswerAddInputObjectGraphType]) {
							addManyStudentQuestionAnswer(list: $list) {
								id
								cmsLifestyleQuestionAnswerOption {
									text
								}
							}
						}
					`,
					variables: {
						list: list
					}
				});
			}

			// if we have response data or list is empty
			// redirect to where you have to redirect
			if ((response && response.data) || isMutationListEmpty) {
				switch (this.type) {
					case 'pretest':
						this.$router.push('/interests');
						break;
					case 'posttest':
						this.$router.push({ name: 'studentSummary' });
						break;
					case 'knowledge':
						this.markKnowledgeQuestionAsAnswered();
						break;
				}
			}
			this.requestMade = false;
		},
		async markKnowledgeQuestionAsAnswered() {
			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($update: StudentInputObjectGraphType!) {
						updateIgnoreNullValuesStudent(update: $update) {
							showKnowledgeQuestionPage
						}
					}
				`,
				variables: {
					update: {
						id: this.userData.studentId,
						showKnowledgeQuestionPage: false,
						isSimulationCompleted: this.isSimulationCompleted,
						dateOfSimulationCompletion: convertDateTimeToUTC()
					}
				}
			});
			if (response.data) {
				this.$store.commit('setLoading', true);
				if (this.isSimulationCompleted) {
					this.$router.push({ name: 'posttest' });
				} else {
					this.$router.push({ name: 'home' });
				}
			}
		},

		isAnswerEmpty(value) {
			return value === null || (typeof value !== 'boolean' && !value.length);
		},
		getQuestionsFilters() {
			const filters = [];

			// Question types are Knowledge or Pre/Post.
			// For correct questions on correct stage (pre or post),
			// the query depends on IsAtEndOfSimulation
			const questionType = this.type == 'knowledge' ? 'Knowledge' : 'Pre/Post';

			// valid state for all types of questions
			filters.push('isActive=true && IsUpperLevelQuestion=true');

			filters.push(`cmsLifestyleQuestionType.questionType="${questionType}"`);

			if (this.type === profileCreationSteps.KNOWLEDGE) {
				filters.push(`lifeStage_ID = "${this.userData.studentLifeStageId}"`);
			}

			// if it is pretest, exclude questions from the end
			if (this.type === profileCreationSteps.PRE_TEST) {
				filters.push(`IsAtEndOfSimulation != true`);
			}

			// if it pretest, include questions form the end
			if (this.type === profileCreationSteps.POST_TEST) {
				filters.push(`IsAtEndOfSimulation = true`);
			}

			return filters.join('&&');
		}
	}
};
</script>
<style lang="scss">
.questionnaire-wrapper {
	header {
		width: 100%;
		button {
			float: right;
		}
	}

	content {
		width: 100%;
		overflow-y: auto;
		flex-grow: 1;
		padding-top: 128px;

		.question-wrapper {
			width: 66%;
			max-width: 850px;
			margin: auto;

			.question-box {
				text-align: center;
			}
		}
	}
	footer {
		display: flex;
		justify-content: space-between;
		align-items: center;

		&.progress-tracker {
			flex-grow: 1;
		}
	}
}
</style>
